import { ref, computed, watch, type Ref } from 'vue';
import { OrganisationPatientService } from '@/services/api';
import { Patient, PaginatedResponse } from '@/models';
import { ISuggestion } from '@/lib';

export interface OrganizationAwarePatientState {
  patient: Ref<Patient | null>;
  patientSearchText: Ref<string>;
  patientSuggestions: Ref<ISuggestion[]>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
}

export interface OrganizationAwarePatientService {
  state: OrganizationAwarePatientState;
  searchPatients: (query: string) => Promise<void>;
  selectPatient: (suggestion: ISuggestion) => Promise<void>;
  fetchPatient: (patientId: string) => Promise<Patient | null>;
  clearPatientData: () => void;
  cleanup: () => void;
}

/**
 * Service that manages patient data with organization awareness.
 * Automatically clears patient data when organization changes to prevent 403 errors.
 */
export function createOrganizationAwarePatientService(
  currentOrganizationId: Ref<string>
): OrganizationAwarePatientService {

  const patient = ref<Patient | null>(null);
  const patientSearchText = ref('');
  const patientSuggestions = ref<ISuggestion[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Reactive patient service that updates when organization changes
  const patientApiService = computed(() =>
    new OrganisationPatientService(currentOrganizationId.value || '')
  );

  // Clear patient data when organization changes
  const organizationWatcher = watch(
    currentOrganizationId,
    (newOrgId, oldOrgId) => {
      if (newOrgId !== oldOrgId && oldOrgId) {
        clearPatientData();
        error.value = null;
      }
    }
  );

  function clearPatientData(): void {
    patient.value = null;
    patientSearchText.value = '';
    patientSuggestions.value = [];
    isLoading.value = false;
  }

  async function searchPatients(query: string): Promise<void> {
    if (!query || query.length < 1) {
      patientSuggestions.value = [];
      return;
    }

    if (!currentOrganizationId.value || currentOrganizationId.value.trim() === '') {
      error.value = 'No organization selected';
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const response = await patientApiService.value.index({
        params: {
          'filter[search]': query,
          'page[size]': 10
        }
      });

      let patients: Patient[] = [];
      const responseData = response.data as any;

      if (responseData && typeof responseData === 'object' && 'data' in responseData && 'meta' in responseData && Array.isArray(responseData.data)) {
        patients = (responseData as PaginatedResponse<Patient[]>).data;
      } else if (Array.isArray(responseData)) {
        patients = responseData as Patient[];
      }

      patientSuggestions.value = patients.map((p: Patient) => ({
        id: p.id,
        label: `${p.first_name} ${p.last_name} ${p.mrn ? `- ID: ${p.mrn}` : ''}`
      }));
    } catch (err: any) {
      error.value = 'Failed to search patients';
      patientSuggestions.value = [];
    } finally {
      isLoading.value = false;
    }
  }

  async function selectPatient(suggestion: ISuggestion): Promise<void> {
    if (!currentOrganizationId.value || currentOrganizationId.value.trim() === '') {
      error.value = 'No organization selected';
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const patientData = await fetchPatient(suggestion.id as string);
      if (patientData) {
        patient.value = patientData;
        patientSearchText.value = suggestion.label;
      }
    } catch (err: any) {
      error.value = 'Failed to load patient details';
      patient.value = null;
      patientSearchText.value = '';
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchPatient(patientId: string): Promise<Patient | null> {
    if (!currentOrganizationId.value || currentOrganizationId.value.trim() === '') {
      throw new Error('No organization selected');
    }

    try {
      const patientData = await patientApiService.value.fetch(patientId);
      return patientData;
    } catch (err: any) {
      if (err?.response?.status === 403) {
        // Patient doesn't exist in current organization - this is expected behavior
        return null;
      }
      throw err;
    }
  }

  function cleanup(): void {
    organizationWatcher();
    clearPatientData();
  }

  return {
    state: {
      patient,
      patientSearchText,
      patientSuggestions,
      isLoading,
      error
    },
    searchPatients,
    selectPatient,
    fetchPatient,
    clearPatientData,
    cleanup
  };
}
